import { DueTreatmentModel } from './due-treatment.model';
import { DueTreatmentStatus } from './medical.enums';
import { isoDatePart, toIsoDateString } from './medical.helpers';

/**
 * Eine Kombinationsimpfung liefert das Backend als eine Zeile je Erreger. Für Liste und Kalender
 * werden sie wieder zu einer Behandlung zusammengefasst.
 */
export interface DueTreatmentGroup {
  treatmentId: number;
  animalId: number;
  animalName: string | null;
  bookNumber: string | null;
  animalCategory: string | null;
  category: DueTreatmentModel['category'];
  /** Alle Erreger dieser Behandlung, in der Reihenfolge des Backends. */
  types: string[];
  preparation: string | null;
  dueAt: string;
  daysUntilDue: number;
  status: DueTreatmentStatus;
  veterinarian: DueTreatmentModel['veterinarian'];
}

const STATUS_ORDER: Record<string, number> = {
  [DueTreatmentStatus.OVERDUE]: 0,
  [DueTreatmentStatus.DUE]: 1,
  [DueTreatmentStatus.UPCOMING]: 2,
};

/** Überfällig zuerst, danach nach Fälligkeit aufsteigend. */
export function compareDueTreatments(a: DueTreatmentGroup, b: DueTreatmentGroup): number {
  const byStatus = (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9);
  if (byStatus !== 0) return byStatus;
  return (a.dueAt ?? '').localeCompare(b.dueAt ?? '') || a.treatmentId - b.treatmentId;
}

export function groupDueTreatments(rows: DueTreatmentModel[]): DueTreatmentGroup[] {
  const groups = new Map<number, DueTreatmentGroup>();

  for (const row of rows) {
    const existing = groups.get(row.treatmentId);
    if (existing) {
      if (row.type && !existing.types.includes(row.type)) existing.types.push(row.type);
      continue;
    }
    groups.set(row.treatmentId, {
      treatmentId: row.treatmentId,
      animalId: row.animalId,
      animalName: row.animalName,
      bookNumber: row.bookNumber,
      animalCategory: row.animalCategory,
      category: row.category,
      types: row.type ? [row.type] : [],
      preparation: row.preparation,
      dueAt: row.dueAt,
      daysUntilDue: row.daysUntilDue,
      status: row.status,
      veterinarian: row.veterinarian,
    });
  }

  return Array.from(groups.values()).sort(compareDueTreatments);
}

/** Fälligkeiten je Tag (`YYYY-MM-DD`), für die Kalenderzellen. */
export function groupDueTreatmentsByDay<T extends DueTreatmentGroup>(groups: T[]): Record<string, T[]> {
  const byDay: Record<string, T[]> = {};
  for (const group of groups) {
    const day = isoDatePart(group.dueAt);
    if (!day) continue;
    (byDay[day] ??= []).push(group);
  }
  return byDay;
}

export interface CalendarDay {
  date: Date;
  iso: string;
  inMonth: boolean;
  isToday: boolean;
}

/**
 * Sechs Wochen ab dem Montag vor dem Monatsersten — immer gleich hoch, damit das Raster beim
 * Blättern nicht springt.
 */
export function buildMonthGrid(year: number, month: number, today: Date = new Date()): CalendarDay[] {
  const first = new Date(year, month, 1);
  // getDay(): Sonntag = 0. Montag als erster Spaltentag.
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(year, month, 1 - offset);
  const todayIso = toIsoDateString(today);

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + index);
    const iso = toIsoDateString(date)!;
    return { date, iso, inMonth: date.getMonth() === month, isToday: iso === todayIso };
  });
}

/** Erster und letzter Tag des Monats als `YYYY-MM-DD`. */
export function monthRange(year: number, month: number): { from: string; to: string } {
  return {
    from: toIsoDateString(new Date(year, month, 1))!,
    to: toIsoDateString(new Date(year, month + 1, 0))!,
  };
}
